import { useMemo } from 'react';
import { RequestStatus as RequestStatuses, HelpRequestStatuses, RequestType } from '@src/utils/enums';
import RequestStatus from './RequestStatus';
import '../../dashboard.scss';
import Table from '@components/table/Table';
import AdditionalInfoLink from './RequestAdditionalInfoLink';
import RequestActions from './RequestActions';
import dayjs from '@src/utils/dayjs';
import { AuthorizedUserPermissions } from '@api/services/authorized-user/types';

import _cloneDeep from 'lodash/cloneDeep';

const helpRequestStatusStyles = {
  [HelpRequestStatuses.Open]: 'open',
  [HelpRequestStatuses.InProgress]: 'in-progress',
};

const displayedHelpStatuses = [HelpRequestStatuses.Closed, HelpRequestStatuses.InProgress, HelpRequestStatuses.Open];
const isHelpStatusDisplayed = (status) => displayedHelpStatuses.includes(status);

const initialColumnOrder = [
  { name: 'actions', alignment: 'center' },
  { name: 'requestStatusContent' },
  { name: 'projectName' },
  { name: 'requestType' },
  { name: 'district' },
  { name: 'modifiedDate' },
  { name: 'daNumber' },
  { name: 'pmName' },
  { name: 'pmEmail' },
];

const initialHeaderData = [
  [
    {
      label: 'Actions',
      name: 'actions',
      rowSpan: 2,
      isSortable: false,
      width: '5.6rem',
    },
    {
      label: 'Status',
      name: 'requestStatus',
      rowSpan: 2,
      width: '4rem',
    },
    {
      label: 'Project Name',
      name: 'projectName',
      rowSpan: 2,
    },
    {
      label: 'Request Type',
      name: 'requestType',
      rowSpan: 2,
      width: '27.5rem',
    },
    {
      label: 'District',
      name: 'district',
      rowSpan: 2,
      width: '6.4rem',
    },
    {
      defaultSortDirection: 'desc',
      label: 'Last Updated',
      name: 'modifiedDate',
      rowSpan: 2,
      className: 'last-updated-date-header',
    },
    {
      label: 'DA Number',
      name: 'daNumber',
      rowSpan: 2,
      width: '5rem',
    },
    {
      label: 'Project Manager',
      name: 'projectManager',
      colSpan: 2,
      isSortable: false,
    },
  ],
  [
    {
      label: 'Name',
      name: 'pmName',
      width: '3rem',
    },
    {
      label: 'Contact Info',
      name: 'pmContactInfo',
      width: '5rem',
    },
  ],
];

const nonArchivableStatuses = [
  RequestStatuses.Acknowledged,
  RequestStatuses.Assigned,
  RequestStatuses.ReRouted,
  RequestStatuses.Submitted,
];

const isStatusArchivable = (status) => !nonArchivableStatuses.includes(status);
const getArchiveState = (request, archived) => {
  const hasActiveHelpInquiry =
    request.requestStatus === RequestStatuses.Draft && isHelpStatusDisplayed(request.helpInquiryStatus);
  const isArchivingDisabled = !isStatusArchivable(request.requestStatus) || hasActiveHelpInquiry;
  const isViolationComplaint = request.requestType === RequestType.ViolationComplaint;
  const canArchiveRequest = isViolationComplaint || request.permission === AuthorizedUserPermissions.Owner;
  const archiveDisabledReason = hasActiveHelpInquiry
    ? `This request cannot be ${archived ? 'unarchived' : 'archived'} while regulator help is open or in progress.`
    : isArchivingDisabled
      ? `This request cannot be ${archived ? 'unarchived' : 'archived'} due to its current status.`
      : !canArchiveRequest
        ? `Only the project owner can ${archived ? 'unarchive' : 'archive'} this request.`
        : '';
  return {
    canArchiveRequest,
    isArchivingDisabled,
    archiveDisabledReason,
  };
};

const getColumnOrder = (hasAir, hasHelpInquiryStatus) => {
  if (hasAir || hasHelpInquiryStatus) {
    const columnOrder = _cloneDeep(initialColumnOrder);
    let insertIndex = 2;

    if (hasHelpInquiryStatus) {
      columnOrder.splice(insertIndex, 0, {
        name: 'helpInquiryStatusContent',
      });
      insertIndex++;
    }

    if (hasAir) {
      columnOrder.splice(insertIndex, 0, {
        name: 'additionalInfoContent',
      });
    }

    return columnOrder;
  }

  return initialColumnOrder;
};

const getHeaderData = (hasAir, isBulkEditing, hasHelpInquiryStatus) => {
  if (!hasAir && !isBulkEditing && !hasHelpInquiryStatus) {
    return initialHeaderData;
  }

  const headerData = _cloneDeep(initialHeaderData);

  let insertIndex = 2;

  if (hasHelpInquiryStatus) {
    headerData[0].splice(insertIndex, 0, {
      label: 'Regulator Help Status',
      name: 'helpInquiryStatus',
      rowSpan: 2,
      className: 'regulator-help-status-header',
    });
    insertIndex++;
  }

  if (hasAir) {
    headerData[0].splice(insertIndex, 0, {
      label: 'Additional Info',
      name: 'additionalInfo',
      rowSpan: 2,
      className: 'additional-info-header',
    });
  }

  if (isBulkEditing) {
    headerData[0][0].label = 'Select';
  }

  return headerData;
};

const DashboardRequestsTable = ({
  rowData: rawRowData,
  onBulkSelection,
  onOpenAdditionalInfo,
  onSingleArchive,
  isBulkEditing = false,
  selectedRequests = {},
  archived,
}) => {
  const hasAir = useMemo(() => {
    if (archived) return false;
    return rawRowData?.some(
      (row) =>
        row.hasOpenAdditionalInfoRequest === true ||
        row.requests?.some((req) => req.hasOpenAdditionalInfoRequest === true)
    );
  }, [archived, rawRowData]);

  const hasHelpInquiryStatus = useMemo(() => {
    if (archived) return false;
    return rawRowData?.some((row) => isHelpStatusDisplayed(row.helpInquiryStatus));
  }, [archived, rawRowData]);

  const rowData = useMemo(
    () =>
      rawRowData.map((request) => {
        const rowKey = `${request.requestID}-${request.id}-${request.version}`;
        const selectKey = `${request.requestID}-${request.version}`;
        const shouldShowHelpInquiryStatus = isHelpStatusDisplayed(request.helpInquiryStatus);
        const helpInquiryStatusStyle = helpRequestStatusStyles[request.helpInquiryStatus] ?? 'default';
        const isHelpInquiryStatusOpen = request.helpInquiryStatus === HelpRequestStatuses.Open;
        const { canArchiveRequest, isArchivingDisabled, archiveDisabledReason } = getArchiveState(request, archived);

        const row = {
          id: request.requestID,
          actions: isBulkEditing ? (
            <div title={archiveDisabledReason}>
              <input
                type='checkbox'
                id={`checkbox-${rowKey}`}
                name={`checkbox-${rowKey}`}
                value={rowKey}
                checked={!!selectedRequests[selectKey]}
                disabled={isArchivingDisabled || !canArchiveRequest}
                onChange={() => onBulkSelection(request.version, request.requestID)}
              />
            </div>
          ) : (
            <RequestActions
              daNumber={request.daNumber}
              district={request.district}
              id={request.id}
              isArchivingDisabled={isArchivingDisabled}
              archived={archived}
              key={`menu-${rowKey}`}
              onArchive={onSingleArchive}
              permission={request.permission}
              projectId={request.projectID}
              projectName={request.projectName}
              requestedPermitType={request.requestedPermitType}
              requestId={request.requestID}
              requestStatus={request.requestStatus}
              requestType={request.requestType}
              version={request.version}
              isHelpInquiryStatusOpen={isHelpInquiryStatusOpen}
              helpInquiryId={request?.helpInquiryID}
            />
          ),
          additionalInfo: request.hasOpenAdditionalInfoRequest,
          additionalInfoContent: request.hasOpenAdditionalInfoRequest ? (
            <AdditionalInfoLink request={request} onOpenAdditionalInfo={onOpenAdditionalInfo} />
          ) : null,
          helpInquiryStatus: request.helpInquiryStatus,
          helpInquiryStatusContent: shouldShowHelpInquiryStatus ? (
            <span className={`help-request-status-tag ${helpInquiryStatusStyle}`}>{request.helpInquiryStatus}</span>
          ) : null,
          modifiedDate: <span className='nowrap-style'>{dayjs.utc(request.modifiedDate).format('YYYY-MM-DD')}</span>,
          daNumber: <span className='nowrap-style'>{request.daNumber}</span>,
          district: request.district?.replace('District', ''),
          rowKey,
          pmEmail: request.pmEmail,
          pmName: <span className='nowrap-style'>{request.pmName}</span>,
          projectName: request.projectName,
          requestStatus: request.requestStatus,
          requestStatusContent: <RequestStatus key={`status-${rowKey}`} value={request.requestStatus} />,
          requestType: request.requestType,
        };

        return row;
      }),
    [isBulkEditing, onBulkSelection, onOpenAdditionalInfo, onSingleArchive, rawRowData, selectedRequests, archived]
  );

  const columnOrder = getColumnOrder(hasAir, hasHelpInquiryStatus);
  const headerData = getHeaderData(hasAir, isBulkEditing, hasHelpInquiryStatus);

  return (
    <Table
      className='dashboard-request-table'
      id='dashboard-requests'
      columnOrder={columnOrder}
      headerData={headerData}
      rowData={rowData}
    />
  );
};
export default DashboardRequestsTable;
